import type { ComputerControlRequest } from '../app/components/computer-control.js';
import type { ProvisioningEnvelope, ReceiptStatus } from '../device-ble/transport.js';
import { validAccountDeviceId } from '../../protocol/device-id.js';
// ASIS desktop UI — typed REST client. Wraps fetch with:
//  - canonical server base (origin only, https or loopback http)
//  - bearer Authorization from a per-call token getter (session controller
//    owns the token; we never persist it)
//  - timeout signals + generation tokens (stale responses
//    dropped silently after logout / server change / 401)
//  - typed error surface; JSON errors `{error,message}` translated
//    into actionable text
//  - no implicit retry of requests
//
// This module is intentionally framework-free. The session controller
// owns the only mutable state (current base, current token, generation).

import type {
  AgentSettings,
  AgentJob,
  ConnectorAssistantRequest,
  ConnectorAssistantResponse,
  ConnectorAuditEvent,
  ConnectorDraft,
  ConnectorPatch,
  ConnectorView,
  ModelRole,
  ModelSettingsPatch,
  ModelSettingsView,
} from './connectors-types.js';
import type { WifiNetwork, WifiNetworkCreate, WifiNetworkList, WifiNetworkPatch } from './wifi-types.js';
import type {
  DeviceInvite,
  DeviceJoinRequest,
  DeviceMembers,
  DeviceWithRole,
  Group,
  GroupCreate,
  GroupDeleteRequest,
  GroupDetail,
  GroupInvite,
  GroupJoinRequest,
  GroupList,
  GroupMember,
  KnowledgeAskRequest,
  KnowledgeAskResponse,
  KnowledgeCreate,
  KnowledgeEntry,
  KnowledgeList,
  KnowledgeScope,
  PeopleList,
  Person,
  PersonOk,
  PersonUpsert,
  RegistrationRequest,
} from './people-types.js';
import type { ComputerStatus } from '../session/controller.js';

export interface Reminder { id: string; title: string; dueAt: number; timeZone: string; status: 'scheduled' | 'completed' | 'cancelled'; revision: number; createdAt: number; updatedAt: number }
export type ReminderDraft = Pick<Reminder, 'title' | 'dueAt' | 'timeZone'>;

export interface CompanionHabitToday { id: string; name: string; doneToday: boolean; streak: number; dueToday: boolean }
export interface CompanionTask { id: string; title: string; dueDay: string | null; done: boolean; doneAt?: number | null; createdAt?: number; position?: number }
export interface CompanionTimer { id: string; deviceId: string | null; kind: 'timer' | 'focus'; label: string; durationS: number; startedAt: number; endsAt: number; state: 'running' | 'finished' | 'cancelled'; createdAt: number }
export interface CompanionToday {
  date: string;
  habits: CompanionHabitToday[];
  tasks: CompanionTask[];
  reminders: Array<{ id: string; title: string; dueAt: number }>;
  timers?: Array<Pick<CompanionTimer, 'id' | 'label' | 'endsAt'>>;
  timer: Pick<CompanionTimer, 'id' | 'label' | 'endsAt'> | null;
  focus: Pick<CompanionTimer, 'id' | 'label' | 'endsAt'> | null;
}
export interface CompanionPrefs { timezone: string; focusMinutes: number; breakMinutes: number; volume: number; idleAnimations: boolean; quietDuringFocus: boolean }
export interface VitalsResult { id:string; deviceId:string; status:'complete'|'signal_insufficient'; capturedAt:number; disclaimer:'estimación de bienestar, no es un diagnóstico'; hrBpm?:number; respirationBpm?:number; f0Hz?:number; jitterLocal?:number; shimmerDb?:number; hnrDb?:number }

export type RequestToken = { cancelled: boolean };

export interface ChatSource { title: string; url?: string; connectorId?: string; operationId?: string; auditId?: string }

export class RequestAbortError extends Error {
  constructor() {
    super('aborted');
    this.name = 'RequestAbortError';
  }
}

export interface ConversationCard { type:'ask'|'credential_request'|'confirm'|'connection_test'; title:string; field?:'url'|'provider'|'paths'; options?:{value:string;label:string}[]; provider?:string; suggestions?:string[]; capabilities?:string[]; counts?:{label:string;count:number}[] }
export interface ConversationState { conversationId:string|null; revision:number; messages:{role:'user'|'assistant';text:string}[]; card?:ConversationCard; work?:{startedAt:number;finishedAt?:number;steps:string[];lastStepAt?:number;interrupted?:boolean;currentStep?:string} }
export type ConversationEvent = {type:'state';state:ConversationState}|{type:'step';label:string;status:'running'|'done';at:number}|{type:'error';message:string};

export interface ApiClientOptions {
  base: string;
  /** returns the current bearer token or null; called per request so the
   *  client never has to be told when the token changes */
  getToken: () => string | null;
  browserSession?: () => boolean;
  /** called whenever the server says the token is no longer valid */
  onUnauthorized: () => void;
  /** called when the client sees a network error / 5xx without a body —
   *  surfaces a transient banner, never auto-retries a write */
  onNetworkError?: (err: Error) => void;
  /** fetch override for tests */
  fetchImpl?: typeof fetch;
}

export interface ApiErrorPayload {
  error?: string;
  message?: string;
  [k: string]: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly payload: ApiErrorPayload | null;
  constructor(status: number, code: string, message: string, payload: ApiErrorPayload | null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.payload = payload;
  }
}

export interface RequestOptions {
  signal?: AbortSignal;
  /** logical operation label for logs and test assertions */
  op?: string;
  /** when true, no Authorization header is added (e.g. /api/status) */
  anonymous?: boolean;
  /** suppress 401 → onUnauthorized (used internally for the very probe
   *  that decides whether the user is logged in) */
  swallowUnauthorized?: boolean;
  /** override the default 12s timeout. bounded to <=25s; aborts via
   *  AbortSignal.timeout. Cancellations from a caller-provided signal
   *  still win (caller signal is combined via AbortSignal.any). */
  timeoutMs?: number;
}

const DEFAULT_TIMEOUT_MS = 12000;
const MAX_TIMEOUT_MS = 25000;

function joinUrl(base: string, path: string): string {
  const b = base.replace(/\/+$/u, '');
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${b}${p}`;
}

function safeJson(text: string): ApiErrorPayload | null {
  if (!text) return null;
  try {
    const j = JSON.parse(text) as unknown;
    if (j && typeof j === 'object') return j as ApiErrorPayload;
  } catch {
    /* fall through */
  }
  return null;
}

export type ConversationKind = 'chat' | 'voice' | 'task';
export type TaskStatus = 'queued' | 'running' | 'needs_input' | 'succeeded' | 'failed' | 'cancelled' | 'interrupted';
export interface ConversationFilter { deviceId?: string; origin?: 'device' | 'app'; kind?: ConversationKind }
export interface ConversationSummary {
  id: string; title: string; createdAt: string; endedAt: string | null;
  /** The unit it was held on (voice) or asked from (task); null for the app. Older servers omit both fields. */
  deviceId?: string | null; kind?: ConversationKind;
  task?: { id: string; status: TaskStatus; progress: string; announced: boolean };
}
export interface TaskEvent {
  id: string; status: TaskStatus; title: string | null; progress: string;
  conversationId: string | null; origin: 'app' | 'device'; deviceId: string | null; updatedAt: string;
}
export type CompanionResource = 'habit' | 'task' | 'timer' | 'focus' | 'prefs' | 'computer';
export type AccountEvent =
  | ({type:'computer.action_requested'} & ComputerControlRequest)
  | {type:'computer.action_cancelled';id:string;orderId:string;clientId:string}
  | { type: 'task.updated' | 'task.finished'; task: TaskEvent }
  | { type: 'companion.updated'; resource: CompanionResource; id?: string }
  | { type: 'desktop.plan_requested' };
const TASK_STATUSES = new Set<TaskStatus>(['queued', 'running', 'needs_input', 'succeeded', 'failed', 'cancelled', 'interrupted']);
const COMPANION_RESOURCES = new Set<CompanionResource>(['habit', 'task', 'timer', 'focus', 'prefs', 'computer']);
const str = (value: unknown, max: number): value is string => typeof value === 'string' && value.length <= max;
/** Whitelists an SSE payload: only known event types and fields reach the UI. */
export function parseAccountEvent(data: string): AccountEvent | null {
  try {
    const raw = JSON.parse(data) as Record<string,unknown> & {task?:Record<string,unknown>};
    if(raw.type==='computer.action_cancelled'){
      if(!str(raw.id,160)||!str(raw.orderId,160)||!str(raw.clientId,160))return null;
      return{type:raw.type,id:raw.id,orderId:raw.orderId,clientId:raw.clientId};
    }
    if(raw.type==='computer.action_requested'){
      if((raw.actor!=='user'&&raw.actor!=='device')||typeof raw.ttlMs!=='number'||!Number.isInteger(raw.ttlMs)||raw.ttlMs<1||raw.ttlMs>12000||!str(raw.id,160)||!str(raw.clientId,160)||!str(raw.orderId,160)||!str(raw.action,32)||!str(raw.userText,16000)||typeof raw.contaminated!=='boolean'||typeof raw.expiresAt!=='number'||!Number.isFinite(raw.expiresAt)||!raw.args||typeof raw.args!=='object'||Array.isArray(raw.args)||JSON.stringify(raw.args).length>20000)return null;
      return{type:raw.type,actor:raw.actor,ttlMs:raw.ttlMs,id:raw.id,clientId:raw.clientId,orderId:raw.orderId,action:raw.action,userText:raw.userText,contaminated:raw.contaminated,expiresAt:raw.expiresAt,args:raw.args as Record<string,unknown>};
    }
    if (raw.type === 'desktop.plan_requested') return { type: 'desktop.plan_requested' };
    if (raw.type === 'companion.updated') {
      if (!COMPANION_RESOURCES.has(raw.resource as CompanionResource) || (raw.id !== undefined && !str(raw.id, 128))) return null;
      return { type: 'companion.updated', resource: raw.resource as CompanionResource, ...(raw.id === undefined ? {} : { id: raw.id }) };
    }
    const task = raw.task;
    if ((raw.type !== 'task.updated' && raw.type !== 'task.finished') || !task || typeof task !== 'object') return null;
    if (!str(task.id, 128) || !TASK_STATUSES.has(task.status as TaskStatus) || !str(task.progress, 4000) || !str(task.updatedAt, 64) ||
        (task.title !== null && !str(task.title, 200)) || (task.conversationId !== null && !str(task.conversationId, 128)) ||
        (task.origin !== 'app' && task.origin !== 'device') || (task.deviceId !== null && !validAccountDeviceId(task.deviceId))) return null;
    return { type: raw.type, task: { id: task.id, status: task.status as TaskStatus, title: task.title as string | null, progress: task.progress,
      conversationId: task.conversationId as string | null, origin: task.origin, deviceId: task.deviceId as string | null, updatedAt: task.updatedAt } };
  } catch { return null; }
}

export class ApiClient {
  computerPresence(clientId:string):Promise<{connected:boolean}>{
    return this.request('POST','/api/companion/computer/presence',{clientId},{op:'computer.presence'});
  }
  computerActionResult(id:string,result:Record<string,unknown>):Promise<unknown>{
    return this.request('POST',`/api/companion/computer/actions/${encodeURIComponent(id)}/result`,result,{op:'computer.result'});
  }

  private readonly opts: ApiClientOptions;
  /** bumped on logout / server change; in-flight responses that complete
   *  with a generation older than this are dropped */
  private generation = 0;
  private readonly fetchFn: typeof fetch;

  constructor(opts: ApiClientOptions) {
    this.opts = opts;
    this.fetchFn = opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  setBase(base: string): void {
    (this.opts as { base: string }).base = base;
  }

  /** invalidate every in-flight request (e.g. logout, server change) */
  invalidate(): void {
    this.generation += 1;
  }

  currentGeneration(): number {
    return this.generation;
  }

  private nextGen(): { generation: number; token: RequestToken } {
    const generation = this.generation;
    const token: RequestToken = { cancelled: false };
    // we don't actually wire the token back into the controller here;
    // the controller tracks AbortControllers itself. The generation
    // number is what gates stale responses.
    return { generation, token };
  }

  private isStale(generation: number): boolean {
    return generation !== this.generation;
  }

  private buildSignal(options: RequestOptions): AbortSignal {
    const requested = typeof options.timeoutMs === 'number' && options.timeoutMs > 0
      ? Math.min(MAX_TIMEOUT_MS, Math.max(1000, options.timeoutMs))
      : DEFAULT_TIMEOUT_MS;
    const timer = AbortSignal.timeout(requested);
    return options.signal ? AbortSignal.any([options.signal, timer]) : timer;
  }

  async request<T>(method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE', path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    const { generation } = this.nextGen();
    const headers: Record<string, string> = {
      Accept: 'application/json',
    };
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    if (!options.anonymous) {
      const tok = this.opts.getToken();
      if (tok) headers['Authorization'] = `Bearer ${tok}`;
    }
    const init: RequestInit = {
      method,
      headers,
      body: body === undefined ? null : JSON.stringify(body),
      signal: this.buildSignal(options),
      credentials: this.opts.browserSession?.() ? 'same-origin' : 'omit',
    };
    let resp: Response;
    try {
      resp = await this.fetchFn(joinUrl(this.opts.base, path), init);
    } catch (err) {
      if (this.isStale(generation)) throw new RequestAbortError();
      if ((err as { name?: string }).name === 'AbortError') throw new RequestAbortError();
      const e = err as Error;
      this.opts.onNetworkError?.(e);
      throw new ApiError(0, 'network', e.message || 'error de red', null);
    }
    if (this.isStale(generation)) {
      // 401/200 alike — anything that arrives after invalidate() is junk
      void resp.text().catch(() => undefined);
      throw new RequestAbortError();
    }
    const text = await resp.text();
    if (this.isStale(generation)) throw new RequestAbortError();
    const payload = safeJson(text);
    if (resp.status === 401) {
      if (!options.anonymous && !options.swallowUnauthorized) this.opts.onUnauthorized();
      throw new ApiError(401, payload?.error ?? 'unauthorized', payload?.message ?? 'sesión caducada', payload);
    }
    if (!resp.ok) {
      throw new ApiError(
        resp.status,
        payload?.error ?? `http_${resp.status}`,
        payload?.message ?? `respuesta no válida (${resp.status})`,
        payload,
      );
    }
    if (payload === null) {
      throw new ApiError(resp.status, 'invalid_json', 'El servidor devolvió una respuesta no válida.', null);
    }
    return payload as T;
  }

  // ---- typed endpoints (one method per documented route) ----

  async getStatus(signal?: AbortSignal): Promise<{ setupRequired: boolean; authRequired: boolean; version: number; registrationEnabled?: boolean }> {
    const status = await this.request<{ setupRequired: boolean; authRequired: boolean; version: number; registrationEnabled?: boolean }>('GET', '/api/status', undefined, { signal, anonymous: true, op: 'status' });
    if (typeof status.setupRequired !== 'boolean' || typeof status.authRequired !== 'boolean') throw new ApiError(502, 'invalid_status', 'Estado del servidor no válido.', null);
    return status;
  }

  getMe(signal?: AbortSignal, bootstrap = false): Promise<{ user: { id: string; username: string; email?: string | null; displayName: string } }> {
    return this.request('GET', '/api/auth/me', undefined, { signal, op: 'me', swallowUnauthorized: bootstrap });
  }

  conversationState(signal?:AbortSignal):Promise<ConversationState> { return this.request('GET','/api/conversation',undefined,{signal}); }
  async conversationStream(path:'turn'|'action'|'credentials', body:Record<string,unknown>, onEvent:(event:ConversationEvent)=>void, signal?:AbortSignal):Promise<void> {
    const generation=this.generation;const token=this.opts.getToken();
    const response=await this.fetchFn(joinUrl(this.opts.base,`/api/conversation/${path}`),{method:'POST',headers:{'Content-Type':'application/json',Accept:'text/event-stream',...(token?{Authorization:`Bearer ${token}`}:{})},body:JSON.stringify(body),credentials:this.opts.browserSession?.()?'same-origin':'omit',signal:signal?AbortSignal.any([signal,AbortSignal.timeout(65000)]):AbortSignal.timeout(65000)});
    if(this.isStale(generation)){void response.body?.cancel();throw new RequestAbortError();}
    if(response.status===401){this.opts.onUnauthorized();throw new RequestAbortError();}
    if(!response.ok||!response.body)throw new Error('No se pudo abrir la conversación.');
    const reader=response.body.getReader();const decoder=new TextDecoder();let buffer='';
    try{for(;;){const part=await reader.read();if(part.done)break;if(this.isStale(generation))throw new RequestAbortError();buffer+=decoder.decode(part.value,{stream:true});if(buffer.length>256000)throw new Error('Respuesta demasiado larga.');let end:number;while((end=buffer.indexOf('\n\n'))>=0){const frame=buffer.slice(0,end);buffer=buffer.slice(end+2);for(const line of frame.split('\n'))if(line.startsWith('data: '))onEvent(JSON.parse(line.slice(6)) as ConversationEvent);}}}finally{await reader.cancel().catch(()=>{});reader.releaseLock();}
  }

  setup(body: { setupToken: string; email: string; displayName: string; password: string }, signal?: AbortSignal): Promise<{ token?: string; session?: 'browser'; user: { id: string; username: string; email?: string | null; displayName: string }; expiresAt: string }> {
    return this.request('POST', '/api/auth/setup', this.opts.browserSession?.() ? { ...body, session: 'browser' } : body, { signal, anonymous: true, op: 'setup' });
  }

  login(body: { email: string; password: string }, signal?: AbortSignal): Promise<{ token?: string; session?: 'browser'; user: { id: string; username: string; email?: string | null; displayName: string }; expiresAt: string }> {
    return this.request('POST', '/api/auth/login', this.opts.browserSession?.() ? { ...body, session: 'browser' } : body, { signal, anonymous: true, op: 'login' });
  }

  logout(signal?: AbortSignal): Promise<{ ok: true }> {
    return this.request('POST', '/api/auth/logout', undefined, { signal, op: 'logout' });
  }

  chat(body: { text: string; requestId: string; conversationId?: string }, signal?: AbortSignal): Promise<{ requestId: string; conversationId: string; text: string; sources?: ChatSource[] }> {
    return this.request('POST', '/api/chat', body, { signal, op: 'chat', timeoutMs: 25000 });
  }

  getProfile(signal?: AbortSignal): Promise<{ id: string; displayName: string; language: 'es'; memoryEnabled: boolean }> {
    return this.request('GET', '/api/profile', undefined, { signal, op: 'profile.get' });
  }

  patchProfile(body: Partial<{ displayName: string; language: 'es'; memoryEnabled: boolean }>, signal?: AbortSignal): Promise<{ id: string; displayName: string; language: 'es'; memoryEnabled: boolean }> {
    return this.request('PATCH', '/api/profile', body, { signal, op: 'profile.patch' });
  }

  listDevices(signal?: AbortSignal): Promise<{ devices: Array<{ id: string; name: string; status: 'online' | 'offline' | 'revoked'; lastSeenAt: string | null; pairedAt: string; role?: 'owner' | 'member' }> }> {
    return this.request('GET', '/api/devices', undefined, { signal, op: 'devices.list' });
  }

  getVitalsLatest(signal?: AbortSignal): Promise<{ result: VitalsResult | null }> {
    return this.request('GET', '/api/vitales/latest', undefined, { signal, op: 'vitales.latest' });
  }

  startVitalsScan(deviceId: string, signal?: AbortSignal): Promise<{ actionId: string; state: 'queued' }> {
    return this.request('POST', '/api/vitales/start', { deviceId }, { signal, op: 'vitales.start' });
  }

  createProvisioningEnvelope(id: string, body: { nonce: string } & ({ ssid: string; psk: string } | { reuse: true } | { networkId: string } | { networkIds: string[] }), signal?: AbortSignal): Promise<{ envelope: ProvisioningEnvelope }> {
    return this.request('POST', `/api/devices/${encodeURIComponent(id)}/provisioning/envelope`, body, { signal, op: 'devices.provisioning.envelope' });
  }

  listWifiNetworks(signal?: AbortSignal): Promise<WifiNetworkList> {
    return this.request('GET', '/api/wifi-networks', undefined, { signal, op: 'wifi.list' });
  }

  createWifiNetwork(body: WifiNetworkCreate, signal?: AbortSignal): Promise<{ network: WifiNetwork }> {
    return this.request('POST', '/api/wifi-networks', body, { signal, op: 'wifi.create' });
  }

  updateWifiNetwork(id: string, expectedRevision: number, patch: WifiNetworkPatch, signal?: AbortSignal): Promise<{ network: WifiNetwork }> {
    return this.request('PATCH', `/api/wifi-networks/${encodeURIComponent(id)}`, { expectedRevision, ...patch }, { signal, op: 'wifi.update' });
  }

  deleteWifiNetwork(id: string, expectedRevision: number, signal?: AbortSignal): Promise<{ ok: true }> {
    return this.request('DELETE', `/api/wifi-networks/${encodeURIComponent(id)}`, { expectedRevision }, { signal, op: 'wifi.delete' });
  }

  verifyProvisioningReceipt(id: string, body: { nonce: string; status: ReceiptStatus; mac: string }, signal?: AbortSignal): Promise<{ verified: true; status: ReceiptStatus }> {
    return this.request('POST', `/api/devices/${encodeURIComponent(id)}/provisioning/verify`, body, { signal, op: 'devices.provisioning.verify' });
  }

  claimDevice(body: { code: string; name?: string }, signal?: AbortSignal): Promise<{ device: { id: string; name: string; status: 'online' | 'offline' | 'revoked'; lastSeenAt: string | null; pairedAt: string } }> {
    return this.request('POST', '/api/devices/claim', body, { signal, op: 'devices.claim' });
  }

  patchDevice(id: string, body: { name: string }, signal?: AbortSignal): Promise<{ device: { id: string; name: string; status: 'online' | 'offline' | 'revoked'; lastSeenAt: string | null; pairedAt: string } }> {
    return this.request('PATCH', `/api/devices/${encodeURIComponent(id)}`, body, { signal, op: 'devices.patch' });
  }

  deleteDevice(id: string, signal?: AbortSignal): Promise<{ ok: true }> {
    return this.request('DELETE', `/api/devices/${encodeURIComponent(id)}`, undefined, { signal, op: 'devices.delete' });
  }

  listMemories(signal?: AbortSignal): Promise<{ memories: Array<{ id: string; text: string; category: string; createdAt: string; updatedAt: string }> }> {
    return this.request('GET', '/api/memories', undefined, { signal, op: 'memories.list' });
  }

  createMemory(body: { text: string; category?: string }, signal?: AbortSignal): Promise<{ memory: { id: string; text: string; category: string; createdAt: string; updatedAt: string } }> {
    return this.request('POST', '/api/memories', body, { signal, op: 'memories.create' });
  }

  patchMemory(id: string, body: { text?: string; category?: string }, signal?: AbortSignal): Promise<{ memory: { id: string; text: string; category: string; createdAt: string; updatedAt: string } }> {
    return this.request('PATCH', `/api/memories/${encodeURIComponent(id)}`, body, { signal, op: 'memories.patch' });
  }

  deleteMemory(id: string, signal?: AbortSignal): Promise<{ ok: true }> {
    return this.request('DELETE', `/api/memories/${encodeURIComponent(id)}`, undefined, { signal, op: 'memories.delete' });
  }

  listReminders(signal?: AbortSignal): Promise<{ reminders: Reminder[]; reminderRevision: number; serverTime: number }> {
    return this.request('GET', '/api/reminders', undefined, { signal, op: 'reminders.list' });
  }

  getCompanionToday(signal?: AbortSignal): Promise<CompanionToday> {
    return this.request('GET', '/api/companion/today', undefined, { signal, op: 'companion.today' });
  }

  createCompanionHabit(name: string, signal?: AbortSignal): Promise<{ habit: CompanionHabitToday }> {
    return this.request('POST', '/api/companion/habits', { name }, { signal, op: 'companion.habits.create' });
  }

  setCompanionHabitCheck(id: string, checked: boolean, signal?: AbortSignal): Promise<{ habit: CompanionHabitToday }> {
    return this.request(checked ? 'POST' : 'DELETE', `/api/companion/habits/${encodeURIComponent(id)}/check`, {}, { signal, op: checked ? 'companion.habits.check' : 'companion.habits.uncheck' });
  }

  listCompanionTasks(signal?: AbortSignal): Promise<{ tasks: CompanionTask[] }> {
    return this.request('GET', '/api/companion/tasks', undefined, { signal, op: 'companion.tasks.list' });
  }

  createCompanionTask(body: { title: string; dueDay?: string }, signal?: AbortSignal): Promise<{ task: CompanionTask }> {
    return this.request('POST', '/api/companion/tasks', body, { signal, op: 'companion.tasks.create' });
  }

  patchCompanionTask(id: string, body: { done: boolean }, signal?: AbortSignal): Promise<{ task: CompanionTask }> {
    return this.request('PATCH', `/api/companion/tasks/${encodeURIComponent(id)}`, body, { signal, op: 'companion.tasks.patch' });
  }

  deleteCompanionTask(id: string, signal?: AbortSignal): Promise<{ ok: true }> {
    return this.request('DELETE', `/api/companion/tasks/${encodeURIComponent(id)}`, undefined, { signal, op: 'companion.tasks.delete' });
  }

  startCompanionTimer(body: { seconds: number; label?: string; deviceId?: string }, signal?: AbortSignal): Promise<{ timer: CompanionTimer; deviceSupported: boolean | null; deviceReason?: 'no_device'|'no_capability'|'busy' }> {
    return this.request('POST', '/api/companion/timers', body, { signal, op: 'companion.timers.start' });
  }

  stopCompanionTimer(id: string, signal?: AbortSignal): Promise<{ timer: CompanionTimer }> {
    return this.request('DELETE', `/api/companion/timers/${encodeURIComponent(id)}`, undefined, { signal, op: 'companion.timers.stop' });
  }

  startCompanionFocus(body: { minutes?: number; label?: string; deviceId?: string }, signal?: AbortSignal): Promise<{ focus: CompanionTimer; deviceSupported: boolean | null; deviceReason?: 'no_device'|'no_capability'|'busy' }> {
    return this.request('POST', '/api/companion/focus', body, { signal, op: 'companion.focus.start' });
  }

  stopCompanionFocus(id: string, signal?: AbortSignal): Promise<{ focus: CompanionTimer }> {
    return this.request('DELETE', `/api/companion/focus/${encodeURIComponent(id)}`, undefined, { signal, op: 'companion.focus.stop' });
  }

  getCompanionPrefs(signal?: AbortSignal): Promise<{ prefs: CompanionPrefs }> {
    return this.request('GET', '/api/companion/prefs', undefined, { signal, op: 'companion.prefs.get' });
  }

  updateCompanionPrefs(prefs: CompanionPrefs, signal?: AbortSignal): Promise<{ prefs: CompanionPrefs }> {
    return this.request('PUT', '/api/companion/prefs', prefs, { signal, op: 'companion.prefs.update' });
  }

  updateComputerStatus(status: ComputerStatus, signal?: AbortSignal): Promise<{ ok: true }> {
    return this.request('PUT', '/api/companion/computer', status, { signal, op: 'companion.computer.update' });
  }

  createReminder(body: ReminderDraft & { requestId: string }, signal?: AbortSignal): Promise<{ reminder: Reminder }> {
    return this.request('POST', '/api/reminders', body, { signal, op: 'reminders.create' });
  }

  patchReminder(id: string, body: Partial<ReminderDraft> & { expectedRevision: number; status?: 'completed' | 'cancelled' }, signal?: AbortSignal): Promise<{ reminder: Reminder }> {
    return this.request('PATCH', `/api/reminders/${encodeURIComponent(id)}`, body, { signal, op: 'reminders.patch' });
  }

  cancelReminder(id: string, expectedRevision: number, signal?: AbortSignal): Promise<{ reminder: Reminder }> {
    return this.request('DELETE', `/api/reminders/${encodeURIComponent(id)}`, { expectedRevision }, { signal, op: 'reminders.cancel' });
  }

  listConversations(signal?: AbortSignal, filter: ConversationFilter = {}): Promise<{ conversations: ConversationSummary[] }> {
    const query = new URLSearchParams();
    if (filter.deviceId) query.set('deviceId', filter.deviceId);
    if (filter.origin) query.set('origin', filter.origin);
    if (filter.kind) query.set('kind', filter.kind);
    const suffix = query.size ? `?${query.toString()}` : '';
    return this.request('GET', `/api/conversations${suffix}`, undefined, { signal, op: 'conversations.list' });
  }

  /** Drops this account's ownership of a unit without revoking its credential: it shows a new screen code. */
  releaseDevice(id: string, signal?: AbortSignal): Promise<{ ok: true; released: true }> {
    return this.request('POST', `/api/devices/${encodeURIComponent(id)}/release`, { confirm: true }, { signal, op: 'devices.release' });
  }

  /** Live account events (Server-Sent Events read with fetch, so the Bearer token works). Resolves when the
   * stream ends; throws ApiError on HTTP failures. Events from before a logout/server change are dropped. */
  async streamEvents(onEvent: (event: AccountEvent) => void, signal: AbortSignal): Promise<void> {
    const generation = this.generation;
    const headers: Record<string, string> = { Accept: 'text/event-stream' };
    const tok = this.opts.getToken();
    if (tok) headers['Authorization'] = `Bearer ${tok}`;
    let resp: Response;
    try {
      resp = await this.fetchFn(joinUrl(this.opts.base, '/api/events'), { method: 'GET', headers, signal, credentials: this.opts.browserSession?.() ? 'same-origin' : 'omit' });
    } catch (err) {
      if (signal.aborted || this.isStale(generation) || (err as { name?: string }).name === 'AbortError') throw new RequestAbortError();
      throw new ApiError(0, 'network', (err as Error).message || 'error de red', null);
    }
    if (this.isStale(generation)) { void resp.body?.cancel().catch(() => undefined); throw new RequestAbortError(); }
    if (!resp.ok || !resp.body) {
      void resp.text().catch(() => undefined);
      if (resp.status === 401) this.opts.onUnauthorized();
      throw new ApiError(resp.status, `http_${resp.status}`, `respuesta no válida (${resp.status})`, null);
    }
    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done || this.isStale(generation)) return;
        buffer += decoder.decode(value, { stream: true });
        if (buffer.length > 65_536) buffer = buffer.slice(-65_536);
        let end: number;
        while ((end = buffer.indexOf('\n\n')) >= 0) {
          const block = buffer.slice(0, end); buffer = buffer.slice(end + 2);
          const data = block.split('\n').filter(line => line.startsWith('data:')).map(line => line.slice(5).trimStart()).join('\n');
          if (!data) continue;
          const event = parseAccountEvent(data);
          if (event && !this.isStale(generation)) onEvent(event);
        }
      }
    } catch (err) {
      if (signal.aborted || this.isStale(generation)) throw new RequestAbortError();
      throw new ApiError(0, 'network', (err as Error).message || 'error de red', null);
    } finally {
      try { reader.releaseLock(); } catch { /* still reading */ }
    }
  }

  getConversation(id: string, signal?: AbortSignal): Promise<{ conversation: ConversationSummary; messages: Array<{ id: string; role: 'user' | 'assistant'; text: string; interrupted: boolean; createdAt: string }> }> {
    return this.request('GET', `/api/conversations/${encodeURIComponent(id)}`, undefined, { signal, op: 'conversations.get' });
  }

  deleteConversation(id: string, signal?: AbortSignal): Promise<{ ok: true }> {
    return this.request('DELETE', `/api/conversations/${encodeURIComponent(id)}`, undefined, { signal, op: 'conversations.delete' });
  }

  exportData(signal?: AbortSignal): Promise<unknown> {
    return this.request('GET', '/api/export', undefined, { signal, op: 'export' });
  }

  deleteData(signal?: AbortSignal): Promise<{ ok: true }> {
    return this.request('DELETE', '/api/data', { confirm: 'BORRAR' }, { signal, op: 'data.delete' });
  }

  async getVoiceConfig(signal?: AbortSignal, legacy = false): Promise<{ provider: string; providerModel: string; sampleRate: number; providerConfigured?: boolean; bootstrapToken?: string }> {
    const value = await this.request<Record<string, unknown>>('GET', legacy ? '/config' : '/api/voice/config', undefined, { signal, anonymous: legacy, op: 'voice.config' });
    const model = value.model ?? value.providerModel;
    if (typeof value.provider !== 'string' || typeof model !== 'string') throw new ApiError(502, 'invalid_config', 'Configuración de voz no válida.', null);
    return { provider: value.provider, providerModel: model, sampleRate: typeof value.sampleRate === 'number' ? value.sampleRate : 24000, ...(typeof value.providerConfigured === 'boolean' ? { providerConfigured: value.providerConfigured } : {}), ...(legacy && typeof value.bootstrapToken === 'string' ? { bootstrapToken: value.bootstrapToken } : {}) };
  }

  // ---- connectors + model roles (Package C) ----

  getAgentSettings(signal?: AbortSignal): Promise<AgentSettings> { return this.request('GET', '/api/agent/settings', undefined, { signal }); }
  patchAgentSettings(autonomy: AgentSettings['autonomy'], signal?: AbortSignal): Promise<AgentSettings> { return this.request('PATCH', '/api/agent/settings', { autonomy, confirm: true }, { signal }); }
  listAgentJobs(signal?: AbortSignal): Promise<{ jobs: AgentJob[] }> { return this.request('GET', '/api/agent/jobs', undefined, { signal }); }
  createAgentJob(message: string, signal?: AbortSignal): Promise<{ job: AgentJob }> { return this.request('POST', '/api/agent/jobs', { message }, { signal }); }
  cancelAgentJob(id: string, signal?: AbortSignal): Promise<{ job: AgentJob }> { return this.request('POST', `/api/agent/jobs/${encodeURIComponent(id)}/cancel`, {}, { signal }); }

  listConnectors(signal?: AbortSignal): Promise<{ connectors: ConnectorView[] }> {
    return this.request('GET', '/api/connectors', undefined, { signal, op: 'connectors.list' });
  }

  createConnector(body: ConnectorDraft, signal?: AbortSignal): Promise<{ connector: ConnectorView }> {
    return this.request('POST', '/api/connectors', body, { signal, op: 'connectors.create' });
  }

  patchConnector(id: string, body: ConnectorPatch, signal?: AbortSignal): Promise<{ connector: ConnectorView }> {
    return this.request('PATCH', `/api/connectors/${encodeURIComponent(id)}`, body, { signal, op: 'connectors.patch' });
  }

  deleteConnector(id: string, signal?: AbortSignal): Promise<{ ok: true }> {
    return this.request('DELETE', `/api/connectors/${encodeURIComponent(id)}`, undefined, { signal, op: 'connectors.delete' });
  }

  setConnectorCredential(id: string, body: { value: string; expectedVersion: number }, signal?: AbortSignal): Promise<{ connector: ConnectorView }> {
    return this.request('POST', `/api/connectors/${encodeURIComponent(id)}/credential`, body, { signal, op: 'connectors.credential' });
  }

  discoverConnector(id: string, signal?: AbortSignal): Promise<{ connector: ConnectorView }> {
    return this.request('POST', `/api/connectors/${encodeURIComponent(id)}/discover`, undefined, { signal, op: 'connectors.discover' });
  }

  testConnector(id: string, body: { operationId: string; args: Record<string, unknown>; expectedVersion: number }, signal?: AbortSignal): Promise<{ connector: ConnectorView; result: unknown; auditId: string }> {
    return this.request('POST', `/api/connectors/${encodeURIComponent(id)}/test`, body, { signal, op: 'connectors.test' });
  }

  activateConnector(id: string, body: { operationIds: string[]; allowOnDevice: boolean; expectedVersion: number; confirm: true }, signal?: AbortSignal): Promise<{ connector: ConnectorView }> {
    return this.request('POST', `/api/connectors/${encodeURIComponent(id)}/activate`, body, { signal, op: 'connectors.activate' });
  }

  setConnectorDeviceAccess(id: string, body: { allow: boolean; expectedVersion: number }, signal?: AbortSignal): Promise<{ connector: ConnectorView }> {
    return this.request('POST', `/api/connectors/${encodeURIComponent(id)}/device-access`, body, { signal, op: 'connectors.deviceAccess' });
  }

  disableConnector(id: string, signal?: AbortSignal): Promise<{ connector: ConnectorView }> {
    return this.request('POST', `/api/connectors/${encodeURIComponent(id)}/disable`, undefined, { signal, op: 'connectors.disable' });
  }

  connectorAssistant(body: ConnectorAssistantRequest, signal?: AbortSignal, timeoutMs?: number): Promise<ConnectorAssistantResponse> {
    return this.request('POST', '/api/connector-assistant', body, { signal, op: 'connectors.assistant', ...(typeof timeoutMs === 'number' ? { timeoutMs } : {}) });
  }

  listConnectorAudit(signal?: AbortSignal): Promise<{ events: ConnectorAuditEvent[] }> {
    return this.request('GET', '/api/connector-audit', undefined, { signal, op: 'connectors.audit' });
  }

  getModelSettings(signal?: AbortSignal): Promise<ModelSettingsView> {
    return this.request('GET', '/api/models', undefined, { signal, op: 'models.get' });
  }

  patchModelSettings(body: ModelSettingsPatch, signal?: AbortSignal): Promise<ModelSettingsView> {
    return this.request('PATCH', '/api/models', body, { signal, op: 'models.patch' });
  }

  testModelRole(body: { role: ModelRole }, signal?: AbortSignal, timeoutMs?: number): Promise<{ ok: boolean; provider: string; model: string; text: string }> {
    return this.request('POST', '/api/models/test', body, { signal, op: 'models.test', ...(typeof timeoutMs === 'number' ? { timeoutMs } : {}) });
  }

  // ---- registration / accounts (people package) ----

  register(body: RegistrationRequest, signal?: AbortSignal): Promise<{ token?: string; session?: 'browser'; user: { id: string; username: string; email?: string | null; displayName: string }; expiresAt?: string }> {
    const payload = this.opts.browserSession?.() ? { ...body, session: 'browser' as const } : body;
    return this.request('POST', '/api/auth/register', payload, { signal, op: 'auth.register', anonymous: true });
  }

  // ---- people (dossier profiles) ----

  listPeople(signal?: AbortSignal): Promise<PeopleList> {
    return this.request('GET', '/api/people', undefined, { signal, op: 'people.list' });
  }

  createPerson(body: PersonUpsert, signal?: AbortSignal): Promise<{ person: Person }> {
    return this.request('POST', '/api/people', body, { signal, op: 'people.create' });
  }

  patchPerson(id: string, body: PersonUpsert, signal?: AbortSignal): Promise<{ person: Person }> {
    return this.request('PATCH', `/api/people/${encodeURIComponent(id)}`, body, { signal, op: 'people.patch' });
  }

  deletePerson(id: string, signal?: AbortSignal): Promise<PersonOk> {
    return this.request('DELETE', `/api/people/${encodeURIComponent(id)}`, undefined, { signal, op: 'people.delete' });
  }

  // ---- knowledge (per person or per group) ----

  listKnowledge(scope: KnowledgeScope, signal?: AbortSignal): Promise<KnowledgeList> {
    if (scope.kind === 'person') return this.request('GET', `/api/people/${encodeURIComponent(scope.id)}/knowledge`, undefined, { signal, op: 'knowledge.list.person' });
    return this.request('GET', `/api/groups/${encodeURIComponent(scope.id)}/knowledge`, undefined, { signal, op: 'knowledge.list.group' });
  }

  createKnowledge(scope: KnowledgeScope, body: KnowledgeCreate, signal?: AbortSignal): Promise<{ entry: KnowledgeEntry }> {
    if (scope.kind === 'person') return this.request('POST', `/api/people/${encodeURIComponent(scope.id)}/knowledge`, body, { signal, op: 'knowledge.create.person' });
    return this.request('POST', `/api/groups/${encodeURIComponent(scope.id)}/knowledge`, body, { signal, op: 'knowledge.create.group' });
  }

  patchKnowledge(scope: KnowledgeScope, entryId: string, body: { title?: string; text?: string }, signal?: AbortSignal): Promise<{ entry: KnowledgeEntry }> {
    if (scope.kind === 'person') return this.request('PATCH', `/api/people/${encodeURIComponent(scope.id)}/knowledge/${encodeURIComponent(entryId)}`, body, { signal, op: 'knowledge.patch.person' });
    return this.request('PATCH', `/api/groups/${encodeURIComponent(scope.id)}/knowledge/${encodeURIComponent(entryId)}`, body, { signal, op: 'knowledge.patch.group' });
  }

  deleteKnowledge(scope: KnowledgeScope, entryId: string, signal?: AbortSignal): Promise<{ ok: true }> {
    if (scope.kind === 'person') return this.request('DELETE', `/api/people/${encodeURIComponent(scope.id)}/knowledge/${encodeURIComponent(entryId)}`, undefined, { signal, op: 'knowledge.delete.person' });
    return this.request('DELETE', `/api/groups/${encodeURIComponent(scope.id)}/knowledge/${encodeURIComponent(entryId)}`, undefined, { signal, op: 'knowledge.delete.group' });
  }

  askKnowledge(req: KnowledgeAskRequest, signal?: AbortSignal, timeoutMs?: number): Promise<KnowledgeAskResponse> {
    return this.request('POST', '/api/knowledge/ask', req, { signal, op: 'knowledge.ask', ...(typeof timeoutMs === 'number' ? { timeoutMs } : {}) });
  }

  // ---- groups ----

  listGroups(signal?: AbortSignal): Promise<GroupList> {
    return this.request('GET', '/api/groups', undefined, { signal, op: 'groups.list' });
  }

  createGroup(body: GroupCreate, signal?: AbortSignal): Promise<{ group: Group }> {
    return this.request('POST', '/api/groups', body, { signal, op: 'groups.create' });
  }

  getGroup(id: string, signal?: AbortSignal): Promise<GroupDetail> {
    return this.request('GET', `/api/groups/${encodeURIComponent(id)}`, undefined, { signal, op: 'groups.get' });
  }

  inviteGroup(id: string, signal?: AbortSignal): Promise<GroupInvite> {
    return this.request('POST', `/api/groups/${encodeURIComponent(id)}/invites`, {}, { signal, op: 'groups.invite' });
  }

  joinGroup(body: GroupJoinRequest, signal?: AbortSignal): Promise<{ group: Group }> {
    return this.request('POST', '/api/groups/join', body, { signal, op: 'groups.join' });
  }

  removeGroupMember(id: string, userId: string, signal?: AbortSignal): Promise<{ ok: true }> {
    return this.request('DELETE', `/api/groups/${encodeURIComponent(id)}/members/${encodeURIComponent(userId)}`, undefined, { signal, op: 'groups.remove' });
  }

  deleteGroup(id: string, body: GroupDeleteRequest, signal?: AbortSignal): Promise<{ ok: true }> {
    return this.request('DELETE', `/api/groups/${encodeURIComponent(id)}`, body, { signal, op: 'groups.delete' });
  }

  // ---- device sharing ----

  listDeviceMembers(id: string, signal?: AbortSignal): Promise<DeviceMembers> {
    return this.request('GET', `/api/devices/${encodeURIComponent(id)}/members`, undefined, { signal, op: 'devices.members' });
  }

  inviteDevice(id: string, signal?: AbortSignal): Promise<DeviceInvite> {
    return this.request('POST', `/api/devices/${encodeURIComponent(id)}/invites`, {}, { signal, op: 'devices.invite' });
  }

  joinDevice(body: DeviceJoinRequest, signal?: AbortSignal): Promise<{ device: DeviceWithRole }> {
    return this.request('POST', '/api/devices/join', body, { signal, op: 'devices.join' });
  }

  removeDeviceMember(id: string, userId: string, signal?: AbortSignal): Promise<{ ok: true }> {
    return this.request('DELETE', `/api/devices/${encodeURIComponent(id)}/members/${encodeURIComponent(userId)}`, undefined, { signal, op: 'devices.remove' });
  }
}
